import React, { useRef, useState, useEffect } from 'react'
import { useTranslation } from '@/i18n'
import { useNavigate } from 'react-router-dom'

const GetStartedPage: React.FC = () => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const videoRef = useRef<HTMLVideoElement>(null)
  const [isPlaying, setIsPlaying] = useState(false) // 默认暂停，所以初始为false

  // 监听视频播放状态变化
  useEffect(() => {
    const video = videoRef.current
    if (!video) return

    const handlePlay = () => setIsPlaying(true)
    const handlePause = () => setIsPlaying(false)

    video.addEventListener('play', handlePlay)
    video.addEventListener('pause', handlePause)

    return () => {
      video.removeEventListener('play', handlePlay)
      video.removeEventListener('pause', handlePause)
    }
  }, [])

  return (
    <div className="bg-gray-950 text-white custom-scrollbar">

      {/* Hero Section */}
      <div className="max-w-7xl mx-auto px-6 py-12">
        <div className="text-center mb-16">
          <h1 className="text-5xl font-bold mb-4 gradient-text">
            Welcome to VisSynth
          </h1>
          <h2 className="text-3xl font-bold mb-4 bg-gradient-to-r from-blue-400 via-purple-400 to-pink-400 bg-clip-text text-transparent">
            {t('tutorial.howCreate')}
          </h2>
          <p className="text-xl text-gray-300 max-w-3xl mx-auto">
            Create stunning AI-generated videos for social platforms in just 4 simple steps
          </p>
        </div>

        {/* Main Content */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left Side: Steps with Simplified Content */}
          <div className="space-y-10">
            {/* Step 1 */}
            <div className="relative group">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/50 flex items-center justify-center text-blue-400 font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                  1
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2 text-blue-400">{t('tutorial.step1.title')}</h3>
                  <p className="text-gray-300">
                    Set your core theme, choose narration voice style, and define resolution for your video
                  </p>
                </div>
              </div>
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-blue-500/50 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Step 2 */}
            <div className="relative group">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-purple-600/20 border border-purple-500/50 flex items-center justify-center text-purple-400 font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                  2
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2 text-purple-400">{t('tutorial.step2.title')}</h3>
                  <p className="text-gray-300">
                    AI intelligently enriches your theme, generates storyboard prompts, images, and narration audio
                  </p>
                </div>
              </div>
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-purple-500/50 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Step 3 */}
            <div className="relative group">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-pink-600/20 border border-pink-500/50 flex items-center justify-center text-pink-400 font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                  3
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2 text-pink-400">{t('tutorial.step3.title')}</h3>
                  <p className="text-gray-300">
                    Edit prompts, convert images to video, add scene transitions, and adjust video clip duration
                  </p>
                </div>
              </div>
              <div className="absolute left-6 top-12 bottom-0 w-0.5 bg-gradient-to-b from-pink-500/50 to-transparent opacity-50 group-hover:opacity-100 transition-opacity duration-300"></div>
            </div>

            {/* Step 4 */}
            <div className="relative group">
              <div className="flex items-start space-x-4">
                <div className="flex-shrink-0 w-12 h-12 rounded-full bg-blue-600/20 border border-blue-500/50 flex items-center justify-center text-blue-400 font-bold text-xl group-hover:scale-110 transition-transform duration-300">
                  4
                </div>
                <div>
                  <h3 className="text-2xl font-semibold mb-2 text-blue-400">{t('tutorial.step4.title')}</h3>
                  <p className="text-gray-300">
                    Synthesize images, videos, audio, and subtitles into a final video with your chosen transitions
                  </p>
                </div>
              </div>
            </div>
          </div>

          {/* Right Side: Video Demo */}
          <div className="relative self-center">
            <div className="bg-gradient-to-br from-gray-800/50 to-gray-900/50 rounded-2xl p-4 border border-gray-700/50 shadow-2xl transform -rotate-2 transition-transform duration-500 group hover:rotate-0">
              <div className="aspect-video rounded-xl overflow-hidden bg-gray-900 relative group">
                {/* Video Demo */}
                <video
                  ref={videoRef}
                  className="w-full h-full object-contain"
                  src="https://data.vissynth.com/video/MU7DySdnaqbnkUJx/oElh0yJ1yw9JUu1p/tutorial.mp4"
                  controls
                  playsInline
                  loop
                />

                {/* Center Play/Pause Button Overlay */}
                <div className="absolute inset-0 flex items-center justify-center pointer-events-none">
                  <button
                    className={`w-20 h-20 rounded-full border-4 flex items-center justify-center transform transition-all duration-300 hover:scale-110 pointer-events-auto ${isPlaying
                      ? 'bg-blue-600/20 border-white/20 hover:bg-blue-600/30 hover:border-white/30'
                      : 'bg-blue-600/90 border-white hover:bg-blue-700'
                      }`}
                    onClick={() => {
                      const video = videoRef.current;
                      if (video) {
                        if (video.paused) {
                          video.play();
                        } else {
                          video.pause();
                        }
                      }
                    }}
                  >
                    {isPlaying ? (
                      // Pause Icon
                      <svg className="w-12 h-12 text-white" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M5 4a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1zm8 0a1 1 0 011 1v10a1 1 0 11-2 0V5a1 1 0 011-1z" />
                      </svg>
                    ) : (
                      // Play Icon
                      <svg className="w-12 h-12 text-white ml-1" fill="currentColor" viewBox="0 0 20 20">
                        <path d="M6.3 2.841A1.5 1.5 0 004 4.11V15.89a1.5 1.5 0 002.3 1.269l9.344-5.89a1.5 1.5 0 000-2.538L6.3 2.84z" />
                      </svg>
                    )}
                  </button>
                </div>
              </div>
            </div>

            {/* Floating Elements */}
            <div className="absolute -top-6 -right-6 w-24 h-24 bg-blue-600/20 rounded-full blur-2xl"></div>
            <div className="absolute -bottom-8 -left-8 w-32 h-32 bg-purple-600/20 rounded-full blur-3xl"></div>
          </div>
        </div>

        {/* CTA */}
        <div className="text-center mt-16">
          <button
            className="px-10 py-4 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg font-bold text-lg hover:shadow-xl hover:shadow-blue-500/20 transition-all duration-300 transform hover:scale-105"
            onClick={() => navigate('/production/video/input')}
          >
            Start Creating Now
          </button>
        </div>
      </div>
    </div>
  )
}

export default GetStartedPage